import { ApiError } from '../lib/api'

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  (typeof window !== 'undefined' ? `${window.location.origin}/api/v1` : 'http://127.0.0.1:8000/api/v1')

/**
 * Vitals API client — patient-entered vital signs with SNOMED mapping.
 *
 * Contract (pinned with Dex — see the ADD-VITALS task brief):
 *
 * - POST /api/v1/patients/{patient_id}/vitals
 *     body { vital_type, value, unit?, effective_datetime? }
 *     → 201 ObservationView (FHIR-aligned Observation)
 *
 * - GET /api/v1/patients/{patient_id}/vitals?vital_type=
 *     → { items: ObservationView[], total }
 *
 * vital_type vocabulary (locked):
 *   blood_pressure_systolic  → SNOMED 163030003
 *   blood_pressure_diastolic → SNOMED 163031004
 *   height                   → SNOMED 1153637007
 *   weight                   → SNOMED 268300005  (patient-observed weight; Dex finalizes)
 *   o2_saturation            → SNOMED 250869005
 *   pain_level               → SNOMED 36329006
 *
 * `entered_by_source` is `"patient"` when the logged-in patient records the
 * reading themselves (this UI flow) and `"clinician"` when a provider enters
 * it at the point of care. The UI renders the source as a visible badge so
 * self-entered values are never confused with clinic measurements.
 */

export type VitalType =
  | 'blood_pressure_systolic'
  | 'blood_pressure_diastolic'
  | 'height'
  | 'weight'
  | 'o2_saturation'
  | 'pain_level'

export type VitalsSource = 'patient' | 'clinician' | 'import'

/**
 * ObservationView — the shape Dex's vitals router returns
 * (apps/api/app/api/v1/vitals.py + app/schemas/vital.py). FHIR R4
 * Observation-aligned: resource_type tag, SNOMED code, valueQuantity-style
 * nested value (value + unit), effective datetime, plus a source tag
 * (patient / clinician / import). The UI renders the source as a visible
 * badge so self-entered values are never confused with clinic measurements.
 */
export type ObservationView = {
  id: string
  resource_type?: 'Observation'
  patient_id: string
  vital_type: VitalType
  snomed_code: string
  value: { value: number; unit: string }
  effective_datetime: string
  entered_by_source: VitalsSource
  created_at?: string | null
  updated_at?: string | null
}

export type VitalListResponse = {
  patient_id: string
  vital_type: string | null
  observations: ObservationView[]
  total: number
}

export type AddVitalPayload = {
  vital_type: VitalType
  value_numeric: number
  unit?: string
  effective_datetime?: string
  entered_by_source?: 'patient' | 'clinician'
}

/**
 * SNOMED CT mapping table — locked to the deployed backend
 * (apps/api/app/models/vital.py). Mirrored in the UI so every row displays
 * the code; the server auto-fills snomed_code from vital_type so clients
 * can't miscode. Display-only here — we never POST a snomed_code.
 */
export const VITAL_SNOMED: Record<VitalType, { code: string; unit: string; min: number; max: number }> = {
  blood_pressure_systolic: { code: '163030003', unit: 'mmHg', min: 30, max: 300 },
  blood_pressure_diastolic: { code: '163031004', unit: 'mmHg', min: 20, max: 200 },
  height: { code: '1153637007', unit: 'cm', min: 30, max: 250 },
  weight: { code: '294636000', unit: 'kg', min: 2, max: 350 },
  o2_saturation: { code: '431314004', unit: '%', min: 0, max: 100 },
  pain_level: { code: '382144000', unit: '{0-10}', min: 0, max: 10 },
}

async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  }
  const response = await fetch(`${API_BASE}${path}`, { ...init, headers })
  if (!response.ok) {
    let detail: unknown = response.statusText
    try {
      const body = await response.json()
      detail = body.detail ?? body
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(response.status, detail)
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/** List a patient's vitals — newest first. Optional vital_type filter. */
export async function listPatientVitals(
  patientId: string,
  options: { vitalType?: VitalType; signal?: AbortSignal } = {},
): Promise<VitalListResponse> {
  const params = new URLSearchParams()
  if (options.vitalType) params.set('vital_type', options.vitalType)
  const qs = params.toString()
  return requestJson<VitalListResponse>(
    `/patients/${patientId}/vitals${qs ? `?${qs}` : ''}`,
    { signal: options.signal },
  )
}

/** Record one vital (patient-entered by default from this UI). */
export async function addPatientVital(
  patientId: string,
  payload: AddVitalPayload,
): Promise<ObservationView> {
  return requestJson<ObservationView>(`/patients/${patientId}/vitals`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}
